/**
 * st-review-k1.js — ST 2026-10-12 (Wrike 4582576365)
 *
 * Social proof carousel K1 in the PDP buy box (snippets/st-review-k1.liquid).
 * Loads the hand-picked Reviews.io reviews (data-ids, in that order) from the same photo endpoint as
 * assets/reviews-io.js and shows only those that are 5 stars, verified and have a photo. Without data-ids
 * (2026-10-13, all PDPs) it takes the first data-max qualifying reviews of the product in API order (newest first). es/fr/it/nl get the
 * Reviews.io auto translation from timeline/data (same rule as reviews-io.js, Wrike 4575801422); if it is late
 * or missing, the original German text stays. Nothing qualifies / API down → the block hides itself.
 */

if (!customElements.get("st-review-k1")) {
  const API_BASE = "https://api.reviews.io/product/review";
  const TIMELINE_BASE = "https://api.reviews.io/timeline/data";
  const TRANSLATE_LOCALES = ["es", "fr", "it", "nl"];
  const TIMELINE_PER_PAGE = 100;
  // Shown reviews are usually recent (page 1 in date_desc); 3 pages is a safety margin, not a full scan.
  const TIMELINE_MAX_PAGES = 3;
  const PHOTO_SIZE = 160;

  const escapeHtml = (str) =>
    String(str ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );

  // Liquid `t` output is HTML-escaped; decode once so escapeHtml stays the only escape step (see reviews-io.js).
  const decodeEntities = (str) => {
    if (typeof str !== "string" || str.indexOf("&") === -1) return str;
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  const getJson = async (url) => {
    try {
      const res = await fetch(url, { credentials: "omit" });
      return res.ok ? await res.json() : null;
    } catch (e) {
      return null;
    }
  };

  const photoUrl = (img) => {
    const raw =
      typeof img === "string" ? img : img?.image || img?.url || img?.src;
    if (!raw) return null;
    return `https://media.reviews.co.uk/resize/create?format=jpg&width=${PHOTO_SIZE}&height=${PHOTO_SIZE}&src=${encodeURIComponent(raw)}`;
  };

  customElements.define(
    "st-review-k1",
    class StReviewK1 extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.ids = (this.dataset.ids || "")
          .split(",")
          .map((id) => Number(id.trim()))
          .filter(Boolean);
        this.max = Number(this.dataset.max) || 6;
        this.store = this.dataset.store;
        this.sku = this.dataset.sku;
        this.locale = this.dataset.locale || "de";
        try {
          this.i18n = JSON.parse(
            this.querySelector("[data-k1-i18n]")?.textContent || "{}",
          );
        } catch (e) {
          this.i18n = {};
        }
        this.track = this.querySelector("[data-k1-track]");
        this.dotsEl = this.querySelector("[data-k1-dots]");
        this.arrows = this.querySelector(".st-k1__arrows");
        this.prevBtn = this.querySelector("[data-k1-prev]");
        this.nextBtn = this.querySelector("[data-k1-next]");
        this.load();
      }

      t(key, vars = {}) {
        let str = decodeEntities(this.i18n[key]) || "";
        for (const [k, v] of Object.entries(vars))
          str = str.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g"), v);
        return str;
      }

      async load() {
        const q = `store=${encodeURIComponent(this.store)}&sku=${encodeURIComponent(this.sku)}`;
        // Same URL as reviews-io.js fetchPhotoReviews → can be served from the HTTP cache.
        // Hand-picked ids are known up front → load translations in parallel; the automatic pick waits for the list.
        const translate = TRANSLATE_LOCALES.includes(this.locale);
        let translationsPromise =
          translate && this.ids.length
            ? this.loadTranslations(q, this.ids)
            : Promise.resolve(null);
        const resp = await getJson(
          `${API_BASE}?${q}&per_page=100&page=1&lang=${encodeURIComponent(this.locale)}&minRating=1&photos=true`,
        );
        const list = Array.isArray(resp?.reviews)
          ? resp.reviews
          : resp?.reviews?.data || [];
        const qualifies = (r) =>
          r &&
          Number(r.rating) === 5 &&
          (r.reviewer?.verified_buyer === "yes" ||
            r.reviewer?.verified_buyer === true) &&
          Array.isArray(r.images) &&
          photoUrl(r.images[0]) &&
          (r.review || "").trim();
        if (this.ids.length) {
          const byId = new Map(
            list.map((r) => [Number(r.product_review_id), r]),
          );
          this.reviews = this.ids.map((id) => byId.get(id)).filter(qualifies);
        } else {
          this.reviews = list.filter(qualifies).slice(0, this.max);
        }
        if (!this.reviews.length) {
          this.hidden = true;
          return;
        }
        if (translate && !this.ids.length)
          translationsPromise = this.loadTranslations(
            q,
            this.reviews.map((r) => Number(r.product_review_id)),
          );
        const translations = await translationsPromise;
        if (translations) {
          for (const r of this.reviews) {
            const text = translations.get(Number(r.product_review_id));
            if (text) {
              r.review = text;
              r.st_translated = true;
            }
          }
        }
        this.render();
      }

      async loadTranslations(q, ids) {
        const found = new Map();
        const wanted = new Set(ids);
        for (
          let page = 1;
          page <= TIMELINE_MAX_PAGES && found.size < wanted.size;
          page++
        ) {
          const resp = await getJson(
            `${TIMELINE_BASE}?type=product_review&${q}&lang=${encodeURIComponent(this.locale)}&sort=date_desc&per_page=${TIMELINE_PER_PAGE}&page=${page}`,
          );
          const items = Array.isArray(resp?.timeline) ? resp.timeline : [];
          for (const item of items) {
            const src = item?._source || {};
            const id = Number(
              String(src._id || item._id || "").replace("product_review-", ""),
            );
            if (wanted.has(id) && src.auto_translated && src.comments)
              found.set(id, src.comments);
          }
          if (items.length < TIMELINE_PER_PAGE) break;
        }
        return found;
      }

      render() {
        const count = this.reviews.length;
        const check =
          '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><polyline points="3 8.5 6.5 12 13 4.5"/></svg>';
        this.track.innerHTML = this.reviews
          .map((r, i) => {
            const first = r.reviewer?.first_name || "";
            const last = r.reviewer?.last_name || "";
            const name = first
              ? `${first}${last ? " " + last.charAt(0) + "." : ""}`
              : "";
            return `
              <div class="st-k1__card" role="group" aria-roledescription="slide" aria-label="${escapeHtml(this.t("slide", { index: i + 1, count }))}">
                <img class="st-k1__photo" src="${escapeHtml(photoUrl(r.images[0]))}" alt="" width="72" height="72" loading="lazy" decoding="async">
                <div class="st-k1__body">
                  <span class="st-k1__stars" role="img" aria-label="${escapeHtml(this.t("rating"))}">★★★★★</span>
                  <p class="st-k1__quote"${r.st_translated ? "" : ' lang="de"'}>${escapeHtml(this.t("quote_open"))}${escapeHtml(r.review.trim())}${escapeHtml(this.t("quote_close"))}</p>
                  <p class="st-k1__meta">
                    ${name ? `<span class="st-k1__name">${escapeHtml(name)}</span><span class="st-k1__sep" aria-hidden="true">·</span>` : ""}
                    <span class="st-k1__verified">${check}${escapeHtml(this.t("verified"))}</span>
                    ${r.st_translated ? `<span class="st-k1__translated">${escapeHtml(this.t("translated"))}</span>` : ""}
                  </p>
                </div>
              </div>`;
          })
          .join("");
        this.cards = Array.from(this.track.children);

        if (count > 1) {
          this.dotsEl.innerHTML = this.reviews
            .map(
              (_, i) =>
                `<button type="button" class="st-k1__dot" data-k1-dot="${i}" aria-label="${escapeHtml(this.t("dot", { index: i + 1 }))}" aria-current="${i === 0}"></button>`,
            )
            .join("");
          this.dots = Array.from(this.dotsEl.children);
          this.dotsEl.addEventListener("click", (e) => {
            const dot = e.target.closest("[data-k1-dot]");
            if (dot) this.goTo(Number(dot.dataset.k1Dot));
          });
          this.arrows.hidden = false;
          this.prevBtn.addEventListener("click", () =>
            this.goTo(this.index - 1),
          );
          this.nextBtn.addEventListener("click", () =>
            this.goTo(this.index + 1),
          );
          this.track.addEventListener("scroll", () => this.onScroll(), {
            passive: true,
          });
        }
        this.index = 0;
        this.update();
      }

      step() {
        return this.cards.length > 1
          ? this.cards[1].offsetLeft - this.cards[0].offsetLeft
          : this.track.clientWidth;
      }

      goTo(i) {
        const index = Math.max(0, Math.min(this.cards.length - 1, i));
        this.track.scrollTo({ left: index * this.step() });
      }

      onScroll() {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          const max = this.track.scrollWidth - this.track.clientWidth;
          // At the scroll end the last card cannot reach the snap start → treat it as the last slide.
          const index =
            this.track.scrollLeft >= max - 2
              ? this.cards.length - 1
              : Math.round(this.track.scrollLeft / this.step());
          if (index !== this.index) {
            this.index = index;
            this.update();
          }
        });
      }

      update() {
        (this.dots || []).forEach((dot, i) =>
          dot.setAttribute("aria-current", String(i === this.index)),
        );
        this.prevBtn.disabled = this.index <= 0;
        this.nextBtn.disabled = this.index >= this.cards.length - 1;
      }
    },
  );
}
